import { useCallback, useMemo } from 'react'
import { useFetchKYC, useInitKYC } from '@/kyc/kycQueries'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import useUserStore from '@/user/userStore'
import { AccountType } from '@/common/types'
import { KycProviders } from '@/kyc/types'

type Result = {
    data:
        | {
              accountType: AccountType
              provider: KycProviders | null
              canInitiate: boolean
          }
        | undefined
    loading: boolean
    saving: boolean
    isSuccess: boolean
    isError: boolean
    mutationData: { url?: string; provider?: KycProviders } | undefined
    onStart: () => void
    onSkip: () => void
}

export function useKycInitLogic(): Result {
    const initKyc = useInitKYC()
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: kyc, isLoading } = useFetchKYC(activeAccountId, { enabled: true })

    const data = useMemo(() => {
        if (!kyc || !user) return undefined

        const accountType = account?.accountType || user?.accountType || AccountType.INDIVIDUAL

        const frankieOneOnboarding = accountType === AccountType.INDIVIDUAL || kyc.provider === KycProviders.FRANKIEONE

        const provider = frankieOneOnboarding ? KycProviders.FRANKIEONE : KycProviders.SWIFTID

        return {
            accountType,
            provider,
            canInitiate: kyc.canInitiateKyc ?? false,
        }
    }, [kyc, user, account?.accountType])

    const onStart = useCallback(() => {
        if (!data) return
        initKyc.mutate({ provider: data.provider!, accountType: data.accountType })
    }, [data, initKyc])

    const onSkip = useCallback(() => {
        // Skip action is just navigation - handled by page layer
    }, [])

    return {
        data,
        loading: isLoading,
        saving: initKyc.isPending,
        isSuccess: initKyc.isSuccess,
        isError: initKyc.isError,
        mutationData: initKyc.data,
        onStart,
        onSkip,
    }
}
